// RFMicroneedlingData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const RFMicroneedlingData: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best RF Microneedling in Dubai | Skin Rejuvenation Treatment",
            "description": "Advanced RF Microneedling treatment in Dubai for skin rejuvenation, wrinkle reduction, and scar improvement. Achieve smoother, firmer skin with our state-of-the-art technology. Book your consultation today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/rf-microneedling/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best RF Microneedling in Dubai | Skin Rejuvenation Treatment",
                "description": "Advanced RF Microneedling treatment in Dubai for skin rejuvenation, wrinkle reduction, and scar improvement. Achieve smoother, firmer skin with our state-of-the-art technology. Book your consultation today.",
                "url": "https://elitebodyhome.com/services/rf-microneedling/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "RF Microneedling Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best RF Microneedling in Dubai | Skin Rejuvenation Treatment",
                "description": "Advanced RF Microneedling treatment in Dubai for skin rejuvenation, wrinkle reduction, and scar improvement. Achieve smoother, firmer skin with our state-of-the-art technology. Book your consultation today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "RF Microneedling"],
        "hero": {
            "title": "RF Microneedling",
            "subtitle": "",
            "description": "Radiofrequency Microneedling is an advanced cosmetic treatment, used to rejuvenate the skin through a highly effective blend of microneedling and radiofrequency energy. With this state-of-the-art technology there are major improvements in skin texture, tone and elasticity, as a treatment for the most common skin problems such as wrinkles, acne scarrings and tightening."
        },
        "howItWorks": {
            "title": "RF Microneedling Procedure",
            "description": "RF Microneedling is an advanced skin rejuvenation procedure that combines microneedling with radiofrequency energy to improve skin texture, tone, and firmness."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Assessment of skin condition and discussion of treatment goals.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Cleansing",
                    "desc": "Thorough cleansing of the treatment area to remove impurities.",
                    "icon": "fa-solid fa-bubbles"
                },
                {
                    "title": "Numbing Cream",
                    "desc": "Application of topical anesthetic to ensure comfort during the procedure.",
                    "icon": "fa-solid fa-spray-can"
                },
                {
                    "title": "RF Microneedling",
                    "desc": "Precision application of radiofrequency energy through micro-needles to stimulate collagen production.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Soothing Mask",
                    "desc": "Application of calming and hydrating mask post-treatment.",
                    "icon": "fa-solid fa-mask"
                },
                {
                    "title": "Aftercare Instructions",
                    "desc": "Detailed guidance on post-treatment care for optimal results.",
                    "icon": "fa-solid fa-book-medical"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of RF Microneedling",
            "subheading": "",
            "para": "RF Microneedling is recognized for the beautiful results it produces in the skin – it has several benefits that help improve often experienced skin issues.",
            "items": [
                {
                    "title": "Collagen Production",
                    "desc": "Stimulates deep collagen renewal for skin tightening and increased elasticity and smoothness.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "Wrinkle Reduction",
                    "desc": "Helps to reduce facial wrinkles, laugh lines and crow's feet.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Improved Skin Texture",
                    "desc": "Tackles the likes of uneven texture, large pores, and dull skin tone.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Skin Tightening",
                    "desc": "Lifts and firms loose skin, particularly under chin, jawline, and on neck and cheeks.",
                    "icon": "fa-solid fa-arrows-up-to-line"
                },
                {
                    "title": "Scar Reduction",
                    "desc": "Provides assistance for smoothing and diminishing scars caused by acne and injuries.",
                    "icon": "fa-solid fa-scroll"
                },
                {
                    "title": "Minimal Downtime",
                    "desc": "Provides a solution with less down time compared to surgery.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "It is important to know if you are a good candidate for radio frequency microneedling prior to receiving your treatment.",
            "items": [
                {
                    "title": "Early Signs of Aging",
                    "desc": "Those with the early appearance of aged skin with fine wrinkles and skin loosening.",
                    "icon": "fa-solid fa-hourglass-start"
                },
                {
                    "title": "Acne Scars",
                    "desc": "Those with acne scars or other scarring.",
                    "icon": "fa-solid fa-scroll"
                },
                {
                    "title": "Uneven Texture",
                    "desc": "Patients desiring texture improvement and reduction in pore size.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Dull Complexion",
                    "desc": "Those who feel they could do with a little strengthening of skin tone and a bit of a brighter complexion.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Non-Invasive Seekers",
                    "desc": "Those who want non-invasive ways to rejuvenate the skin with little to no downtime.",
                    "icon": "fa-solid fa-hand-sparkles"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "You need to prepare for the procedure to get the best results and reduce the risk of side effects.",
            "items": [
                {
                    "title": "Avoid Sun Exposure",
                    "desc": "Avoid sun or tanning bed for at least two weeks prior to the treatment.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Stop Certain Products",
                    "desc": "Don't use retinoids, exfoliants or any harsh skincare in the days leading up to your appointment.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Hydrate Well",
                    "desc": "Drink a lot of water during the days leading up to your session to keep your skin well-hydrated.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Consult Your Provider",
                    "desc": "Let your doctor know about any medicines or supplements you're taking that you may need to stop.",
                    "icon": "fa-solid fa-user-doctor"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Radiofrequency Microneedling results can be both immediate and progressive, typically seeing more supple, lifted skin within a week.",
            "items": [
                {
                    "title": "Immediate Improvement",
                    "desc": "Initial glow and improved skin texture visible shortly after treatment.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "Progressive Enhancement",
                    "desc": "Continued improvement as collagen production increases over weeks.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Long-Term Benefits",
                    "desc": "Sustained results with proper skincare and maintenance treatments.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare",
            "subheading": "",
            "para": "Proper aftercare is essential for optimal results and recovery.",
            "items": [
                {
                    "title": "Sun Protection",
                    "desc": "Stay out of the sun for a week after the treatment and apply broad-spectrum sunscreen daily.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Moisturize",
                    "desc": "Keep the skin moist with a mild, non-irritating moisturizer.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "Avoid Harsh Products",
                    "desc": "Stay away from heavy skincare products such as retinol or exfoliators for a few days.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Limit Exercise",
                    "desc": "Avoid vigorous workouts and activities that make you sweat for 24 hours.",
                    "icon": "fa-solid fa-dumbbell"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does the treatment take?",
                    "answer": "The treatment typically lasts 30 to 60 minutes, depending on the size of the area being treated."
                },
                {
                    "question": "Is Radiofrequency Microneedling painful?",
                    "answer": "Most patients experience minimal discomfort during the procedure. A topical numbing cream is applied beforehand to reduce any potential discomfort."
                },
                {
                    "question": "How many sessions are required?",
                    "answer": "Most patients need 3 to 4 sessions, spaced about a month apart, for optimal results. Your provider will create a customized treatment plan based on your skin concerns."
                },
                {
                    "question": "What is the downtime after treatment?",
                    "answer": "Downtime is minimal, with most patients experiencing redness and mild swelling for 1 to 2 days post-treatment. You can usually resume normal activities after 24 hours."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/New-Service-Bansfefner-Image-3-scaled.jpg",
            "beforeAfter": [
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/PRP-Facial-Treatment-Dubaid-2.jpg", "alt": "dermapen dubai" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Acne-Treatment-Dubai-2.jpg", "alt": "dermapen dubai" },
                { "src": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/karismabefore-and-after-in-ebh-2.jpg", "alt": "dermapen dubai" }
            ],
            "introductionImage": "https://example.com/intro-image.jpg"
        },
        "product": {
            "id": 501,
            "title": "RF Microneedling Package",
            "price": 999,
            "description": "Comprehensive RF Microneedling package including consultation, treatment sessions, and follow-up care. Our expert team offers advanced skin rejuvenation solutions tailored to your needs."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل علاج الميكرونيدلينغ بالراديو فريكونسي في دبي | تجديد البشرة",
            "description": "علاج متقدم للميكرونيدلينغ بالراديو فريكونسي في دبي لتجديد البشرة، تقليل التجاعيد وتحسين الندوب. احصل على بشرة أكثر نعومة وتماسكًا بتقنيتنا المتطورة. احجز استشارتك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/rf-microneedling/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل علاج الميكرونيدلينغ بالراديو فريكونسي في دبي | تجديد البشرة",
                "description": "علاج متقدم للميكرونيدلينغ بالراديو فريكونسي في دبي لتجديد البشرة، تقليل التجاعيد وتحسين الندوب. احصل على بشرة أكثر نعومة وتماسكًا بتقنيتنا المتطورة. احجز استشارتك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/rf-microneedling/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج الميكرونيدلينغ بالراديو فريكونسي دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل علاج الميكرونيدلينغ بالراديو فريكونسي في دبي | تجديد البشرة",
                "description": "علاج متقدم للميكرونيدلينغ بالراديو فريكونسي في دبي لتجديد البشرة، تقليل التجاعيد وتحسين الندوب. احصل على بشرة أكثر نعومة وتماسكًا بتقنيتنا المتطورة. احجز استشارتك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "الميكرونيدلينغ بالراديو فريكونسي"],
        "hero": {
            "title": "الميكرونيدلينغ بالراديو فريكونسي",
            "subtitle": "",
            "description": "الميكرونيدلينغ بالراديو فريكونسي هو علاج تجميلي متقدم يُستخدم لتجديد البشرة من خلال مزيج فعال للغاية من الميكرونيدلينغ وطاقة الراديو فريكونسي. باستخدام هذه التقنية المتطورة، يمكن تحقيق تحسينات كبيرة في ملمس البشرة ولونها ومرونتها، كعلاج لأكثر مشاكل البشرة شيوعًا مثل التجاعيد وندبات حب الشباب والشد."
        },
        "howItWorks": {
            "title": "إجراء الميكرونيدلينغ بالراديو فريكونسي",
            "description": "الميكرونيدلينغ بالراديو فريكونسي هو إجراء متقدم لتجديد البشرة يجمع بين الميكرونيدلينغ وطاقة الراديو فريكونسي لتحسين ملمس البشرة ولونها وتماسكها."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "تقييم حالة البشرة ومناقشة أهداف العلاج.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "التنظيف",
                    "desc": "تنظيف شامل لمنطقة العلاج لإزالة الشوائب.",
                    "icon": "fa-solid fa-bubbles"
                },
                {
                    "title": "كريم التخدير",
                    "desc": "تطبيق مخدر موضعي لضمان الراحة أثناء الإجراء.",
                    "icon": "fa-solid fa-spray-can"
                },
                {
                    "title": "الميكرونيدلينغ بالراديو فريكونسي",
                    "desc": "تطبيق دقيق لطاقة الراديو فريكونسي عبر الإبر الدقيقة لتحفيز إنتاج الكولاجين.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "ماسك مهدئ",
                    "desc": "تطبيق قناع مهدئ ومرطب بعد العلاج.",
                    "icon": "fa-solid fa-mask"
                },
                {
                    "title": "تعليمات ما بعد العلاج",
                    "desc": "إرشادات مفصلة عن العناية بعد العلاج لتحقيق أفضل النتائج.",
                    "icon": "fa-solid fa-book-medical"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد الميكرونيدلينغ بالراديو فريكونسي",
            "subheading": "",
            "para": "يُعرف الميكرونيدلينغ بالراديو فريكونسي بالنتائج الجميلة التي ينتجها في البشرة - حيث يتمتع بالعديد من الفوائد التي تساعد في تحسين المشكلات الجلدية الشائعة.",
            "items": [
                {
                    "title": "إنتاج الكولاجين",
                    "desc": "يحفز تجديد الكولاجين العميق لشد البشرة وزيادة مرونتها ونعومتها.",
                    "icon": "fa-solid fa-dna"
                },
                {
                    "title": "تقليل التجاعيد",
                    "desc": "يساعد في تقليل تجاعيد الوجه وخطوط الضحك وعلامات الزمن.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "يعالج الملمس غير المتجانس، المسام الكبيرة، والبشرة الباهتة.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "شد البشرة",
                    "desc": "يرفع ويشد البشرة المترهلة، خاصة تحت الذقن، خط الفك، والرقبة والخدين.",
                    "icon": "fa-solid fa-arrows-up-to-line"
                },
                {
                    "title": "تقليل الندوب",
                    "desc": "يوفر مساعدة في تنعيم وتقليل الندوب الناتجة عن حب الشباب والإصابات.",
                    "icon": "fa-solid fa-scroll"
                },
                {
                    "title": "فترة تعافي قصيرة",
                    "desc": "يوفر حلاً بفترة تعافي أقل مقارنة بالجراحة.",
                    "icon": "fa-solid fa-clock"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "من المهم معرفة ما إذا كنت مرشحًا جيدًا للميكرونيدلينغ بالراديو فريكونسي قبل تلقي العلاج.",
            "items": [
                {
                    "title": "علامات الشيخوخة المبكرة",
                    "desc": "الأشخاص الذين يعانون من ظهور مبكر لعلامات الشيخوخة مع تجاعيد دقيقة وترهل الجلد.",
                    "icon": "fa-solid fa-hourglass-start"
                },
                {
                    "title": "ندوب حب الشباب",
                    "desc": "الأشخاص الذين يعانون من ندوب حب الشباب أو ندوب أخرى.",
                    "icon": "fa-solid fa-scroll"
                },
                {
                    "title": "ملمس غير متجانس",
                    "desc": "المرضى الراغبون في تحسين الملمس وتقليل حجم المسام.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "بشرة باهتة",
                    "desc": "الأشخاص الذين يشعرون أنهم بحاجة إلى تقوية لون البشرة قليلاً وإشراقها.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "الباحثون عن علاجات غير جراحية",
                    "desc": "الأشخاص الذين يريدون طرقًا غير جراحية لتجديد البشرة مع القليل من وقت التعافي أو بدونه.",
                    "icon": "fa-solid fa-hand-sparkles"
                }
            ]
        },
        "preparationTips": {
            "heading": "التحضير",
            "subheading": "",
            "para": "تحتاج إلى التحضير للإجراء للحصول على أفضل النتائج وتقليل مخاطر الآثار الجانبية.",
            "items": [
                {
                    "title": "تجنب التعرض للشمس",
                    "desc": "تجنب الشمس أو أسرّة التسمير لمدة أسبوعين على الأقل قبل العلاج.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "إيقاف بعض المنتجات",
                    "desc": "لا تستخدمي الريتينويدات أو المقشرات أو أي مستحضرات عناية قاسية في الأيام التي تسبق موعدك.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "الحفاظ على الترطيب",
                    "desc": "اشرب الكثير من الماء في الأيام التي تسبق جلسة العلاج للحفاظ على ترطيب بشرتك جيدًا.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "استشارة الطبيب",
                    "desc": "أخبر طبيبك عن أي أدوية أو مكملات تتناولها قد تحتاج إلى التوقف عنها.",
                    "icon": "fa-solid fa-user-doctor"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "يمكن أن تكون نتائج الميكرونيدلينغ بالراديو فريكونسي فورية وتدريجية، حيث يتم ملاحظة بشرة أكثر نعومة ومرونة في غضون أسبوع.",
            "items": [
                {
                    "title": "تحسن فوري",
                    "desc": "إشراق أولي وتحسن في ملمس البشرة مرئي بعد العلاج بفترة قصيرة.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "تحسن تدريجي",
                    "desc": "استمرار التحسن مع زيادة إنتاج الكولاجين على مدار الأسابيع.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "فوائد طويلة الأمد",
                    "desc": "نتائج مستدامة مع العناية المناسبة بالبشرة وجلسات الصيانة.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "العناية بعد العلاج",
            "subheading": "",
            "para": "العناية المناسبة بعد العلاج ضرورية للحصول على أفضل النتائج والتعافي.",
            "items": [
                {
                    "title": "الحماية من الشمس",
                    "desc": "تجنب الشمس لمدة أسبوع بعد العلاج واستخدم واقي شمس واسع الطيف يوميًا.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "الترطيب",
                    "desc": "حافظ على ترطيب البشرة بمرطب لطيف غير مهيج.",
                    "icon": "fa-solid fa-droplet"
                },
                {
                    "title": "تجنب المنتجات القاسية",
                    "desc": "ابتعد عن مستحضرات العناية القوية مثل الريتينول أو المقشرات لبضعة أيام.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "تقييد التمارين",
                    "desc": "تجنب التمارين الشاقة والأنشطة التي تجعلك تتعرق لمدة 24 ساعة.",
                    "icon": "fa-solid fa-dumbbell"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق الجلسة؟",
                    "answer": "تستغرق الجلسة عادةً من 30 إلى 60 دقيقة، حسب حجم المنطقة المعالجة."
                },
                {
                    "question": "هل الميكرونيدلينغ بالراديو فريكونسي مؤلم؟",
                    "answer": "يشعر معظم المرضى بانزعاج بسيط أثناء الإجراء. يتم تطبيق كريم مخدر موضعي مسبقًا لتقليل أي انزعاج محتمل."
                },
                {
                    "question": "كم عدد الجلسات المطلوبة؟",
                    "answer": "يحتاج معظم المرضى من 3 إلى 4 جلسات، بفارق شهر تقريبًا بينها، للحصول على أفضل النتائج. سيضع لك المختص خطة علاج مخصصة بناءً على مشاكل بشرتك."
                },
                {
                    "question": "ما هي فترة التعافي بعد العلاج؟",
                    "answer": "فترة التعافي قصيرة، حيث يعاني معظم المرضى من احمرار وتورم خفيف لمدة يوم إلى يومين بعد العلاج. يمكنك عادةً استئناف الأنشطة العادية بعد 24 ساعة."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}